<template>
    <div>

        <rkm-form
            ref="Form"
            show-legend
            v-if="loaded"
        >
            <rkm-area
                type="fieldset"
                title="Identificação do paciente"
            >
                <FormCidadao
                    :data="{ form, model }"
                />
            </rkm-area>

            <rkm-area
                type="fieldset"
                title="Profissional responsável"
                show-legend
            >
                <FormProfissional
                    :data="{ form, model }"
                />
            </rkm-area>

            <rkm-area
                type="fieldset"
                title="Atendimento"
            >
                <FormAtendimento
                    :data="{ form, model }"
                />
            </rkm-area>

            <rkm-area
                type="fieldset"
                title="Motivo da visita"
            >
                <FormMotivo
                    :data="{ form, model }"
                />
            </rkm-area>

            <FormComplemento
                :data="{ form, model }"
            />
        </rkm-form>

        <Modal
            ref="Modal"
            origem="atendimento"
            :residentes="residentes"
            @closeModal="closeModal"
            :data="{ form, model }"
        />
    </div>
</template>

<script>

// Mixins
import { repository, stringCase, data, object, formats, error } from "@/mixins";

// Vuex.
import { mapActions } from "vuex";

// Components
import FormCidadao from './components/cidadao/Form.vue'
import FormProfissional from './components/profissional/Form.vue'
import FormAtendimento from './components/atendimento/Form.vue'
import FormMotivo from './components/motivo/Form.vue'
import FormComplemento from './components/complemento/Form.vue'
import Modal from './Modal.vue'

export default {
    components: {
        FormCidadao,
        FormProfissional,
        FormAtendimento,
        FormMotivo,
        FormComplemento,
        Modal
    },
    computed: {
        validations() {

            return {
                complemento: { max: 1000 },
                peso: { min_value: 0.5, max_value: 500 },
                altura: { min_value: 20, max_value: 250 },
            }
        },
    },
    created() {

        this.repositories = {
            visitaDomiciliar: this.getConectaSusRepository('atencaoBasica.visitaDomiciliar')
        }

        this.load()
    },
    data: () => ({
        additionals: null,
        form: {},
        model: {},
        loaded: false,
        residentes: []
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            if (this.id) {

                this.repositories.visitaDomiciliar.edit(this.id)
                    .then(response => {
                        this.additionals = Object.assign(response.data.additionals)
                        this.model = Object.assign(response.data.model)
                        this.form = Object.assign(response.data.form)

                        this.loaded = true

                        this.$emit('loaded', {additionals: this.additionals, model: this.model})
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            } else {

                this.repositories.visitaDomiciliar.show(this.pacienteId)
                    .then(response => {
                        this.additionals = Object.assign(response.data.additionals)
                        this.model = Object.assign(response.data.model)
                        this.form = Object.assign(response.data.form)

                        this.loaded = true

                        this.$emit('loaded', {additionals: this.additionals, model: this.model})
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
        },
        async save() {

            if(this.hasVisitasDuplicadas()){
                this.showWarningNotification("Já existe uma visita domiciliar cadastrada para este profissional, turno e data.")
                return
            }

            const valid = await this.$refs.Form.validate()
            if (!valid) return
            this.setLoading()

            try {
                const { data } = await this.repositories.visitaDomiciliar.store(this.prepareData())
                await this.getResidentes()

                if (this.residentes.length > 0) {
                    this.showConfirmation(
                        'Registro salvo. Deseja replicar as informações para outros residentes?',
                        async () => {
                            this.$refs.Modal.open({ id: this.model.cidadao.pacienteId })
                        },
                        async () => {
                            this.showSaveNotification()
                            this.$emit('save', data)
                        }
                    )
                } else {
                    this.showSaveNotification()
                    this.$emit('save', data)
                }

            } catch (error) {
                this.handleError(error)
            } finally {
                this.unsetLoading()
            }
        },
        async getResidentes() {
            this.setLoading()

            const request = {
                pacienteId: this.model.cidadao.pacienteId,
            }

            try {
                const response = await this.repositories.visitaDomiciliar.getResidentes(this.camelObjectKeysToSnakeRecursive(request))
                this.residentes = Object.assign(response.data)
            } catch (error) {
                this.handleError(error)
            } finally {
                this.unsetLoading()
            }
        },

        hasVisitasDuplicadas(){
            const profissionalId = Number(this.model.cadastro.usuario.id)
            const turnoId = Number(this.model.atendimento.turnoId)
            const atendimentoId = Number(this.model.atendimento.id)
            const data =  this.getFormatedDate(this.model.cadastro.dataUsuarioResponsavel, 'YYYY-MM-DD')

            const duplicadas = this.visitasRealizadas.filter(visita => {
                return Number(visita.profissionalId) === profissionalId  &&
                    Number(visita.turnoId) === turnoId &&
                    visita.data === data &&
                    Number(visita.id) != atendimentoId
            })

            return duplicadas.length > 0
        },  
        prepareData() {

            const pacienteId = this.model.cidadao.pacienteId
            const cadastro = this.clone(this.model.cadastro)

            cadastro.usuarioId = cadastro.usuario.id
            cadastro.dataUsuarioResponsavel = this.getFormatedDate(cadastro.dataUsuarioResponsavel, 'YYYY-MM-DD')

            const atendimento = this.clone(this.model.atendimento)
            const complemento = this.clone(this.model.complemento)
            const motivo = this.clone(this.model.motivo)

            return this.camelObjectKeysToSnakeRecursive({
                pacienteId, cadastro, atendimento, motivo, complemento
            })
        },
        closeModal() {
            this.$emit('save')
        }
    },
    mixins: [repository, stringCase, data, object, formats, error],
    props: {
        id: { type: Number, default: null },
        pacienteId: { type: Number, default: null },
        visitasRealizadas: { type: Array, default: null}
    },
}
</script>